import { Alert, Button, Card, Flex, Input, Space, Tag, Typography } from 'antd';
import { reviewSectionLabel } from '@/features/inventorization/review-i18n';
import { useEffect, useMemo, useState } from 'react';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { readBuildingFloorValue } from '@/features/inventorization/building-floor-options';
import {
  FLOORPLAN_PLACEMENT_STATES,
  type InventorizationPosition,
} from '@/features/inventorization/domain/inventorization-position';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { readBuildingNameLabel } from '@/features/inventorization/placement-options';
import {
  isSameBuilding,
  isSameFloor,
  type PlanPoint,
  planPointToFloorplanCoordinates,
  readFloorplanCoordinatesPoint,
  readFloorplanDimensions,
  readMeterFloorplanPoint,
} from '@/features/inventorization/position/floorplan-marker-geometry';
import { useNewMeterLocationStep } from '@/features/inventorization/useNewMeterLocationStep';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import {
  FloorplanPinSummary,
  FloorplanPlacementPanel,
} from '@/routers/inventorization/components/position/FloorplanPlacementPanel';
import { ReviewStatusTag } from '@/routers/inventorization/components/ReviewStatusTag';
import type { CaptureSession } from '@/shared/capture/capture-session';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

export function FloorplanPositionFieldWizard({
  field,
  onBack,
  onSavePosition,
  position,
  session,
}: {
  field: InventorizeReviewField;
  onBack: () => void;
  onSavePosition: (patch: Partial<InventorizationPosition>) => void;
  position: InventorizationPosition;
  session: CaptureSession;
}) {
  const { activeLocation } = useStartupSession();
  const { bootstrap } = useLocationBootstrap();
  const selectedBuilding = (bootstrap?.buildings ?? []).find(building => building.name === position.building);
  const selectedBuildingId = selectedBuilding ? String(selectedBuilding.id) : null;
  const floorValue = useMemo(() => readBuildingFloorValue(position.floor), [position.floor]);
  const { error, floorplanImageUrl, floorplanRecord, isLoadingFloorplan, saveLocalFloorplan } = useNewMeterLocationStep(
    {
      activeLocation,
      buildingId: selectedBuildingId,
      floor: floorValue,
    },
  );
  const floorplanDimensions = useMemo(() => readFloorplanDimensions(floorplanRecord), [floorplanRecord]);
  const initialMarker = useMemo(
    () => readFloorplanCoordinatesPoint(position.floorplanLat, position.floorplanLng, floorplanDimensions),
    [floorplanDimensions, position.floorplanLat, position.floorplanLng],
  );
  const [selectedMarker, setSelectedMarker] = useState<PlanPoint | null>(initialMarker);
  const [notPlacedReason, setNotPlacedReason] = useState(position.floorplanNotPlacedReason);
  const [floorplanUploadError, setFloorplanUploadError] = useState<string | null>(null);
  const [isSavingFloorplan, setIsSavingFloorplan] = useState(false);
  const existingPins = useMemo(
    () =>
      (bootstrap?.existingMeters ?? [])
        .filter(meter => meter._id !== session._id)
        .filter(meter => isSameFloor(meter.floorName, floorValue, position.floor))
        .filter(meter => isSameBuilding(meter, selectedBuildingId))
        .map(meter => readMeterFloorplanPoint(meter, floorplanDimensions))
        .filter((point): point is PlanPoint => Boolean(point)),
    [bootstrap?.existingMeters, floorplanDimensions, floorValue, position.floor, selectedBuildingId, session._id],
  );

  useEffect(() => {
    setSelectedMarker(initialMarker);
    setNotPlacedReason(position.floorplanNotPlacedReason);
  }, [initialMarker, position.floorplanNotPlacedReason]);

  function savePlaced() {
    if (!selectedMarker) {
      return;
    }

    const floorplanCoordinates = planPointToFloorplanCoordinates(selectedMarker, floorplanDimensions);

    if (!floorplanCoordinates) {
      return;
    }

    onSavePosition({
      floorplanBlobId: floorplanRecord?.pendingUpload ? floorplanRecord.id : position.floorplanBlobId,
      floorplanLat: floorplanCoordinates.lat,
      floorplanLng: floorplanCoordinates.lng,
      floorplanNotPlacedReason: '',
      floorplanState: FLOORPLAN_PLACEMENT_STATES.placed,
    });
    onBack();
  }

  function saveNotPlaced() {
    onSavePosition({
      floorplanBlobId: floorplanRecord?.pendingUpload ? floorplanRecord.id : position.floorplanBlobId,
      floorplanLat: null,
      floorplanLng: null,
      floorplanNotPlacedReason: notPlacedReason,
      floorplanState: floorplanImageUrl
        ? FLOORPLAN_PLACEMENT_STATES.notPlaced
        : FLOORPLAN_PLACEMENT_STATES.notAvailable,
    });
    onBack();
  }

  async function handleFloorplanUpload(file: File | null) {
    if (!file) {
      return;
    }

    setFloorplanUploadError(null);
    setIsSavingFloorplan(true);

    try {
      await saveLocalFloorplan(file);
      setNotPlacedReason('');
    } catch (uploadError) {
      setFloorplanUploadError(
        uploadError instanceof Error
          ? uploadError.message
          : translateInventoryText(
              'app.inventory.position.floorplan.saveLocalError',
              'Unable to save floorplan locally.',
            ),
      );
    } finally {
      setIsSavingFloorplan(false);
    }
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Flex align="flex-start" gap="middle" justify="space-between" wrap>
          <div>
            <Typography.Text type="secondary">{reviewSectionLabel(field.section)}</Typography.Text>
            <Typography.Title level={3} style={{ marginTop: 0 }}>
              {field.label}
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              {translateInventoryText(
                'app.inventory.position.floorplan.reviewDescription',
                'Tap the floorplan to place this meter, or drag the marker to refine it. Grey pins are already cached meters on the same floor.',
              )}
            </Typography.Paragraph>
          </div>
          <ReviewStatusTag status={field.status} />
        </Flex>

        {error ? <Alert closable message={error} showIcon type="warning" /> : null}

        <FloorplanPlacementPanel
          alertsClosable
          emptyDescription={translateInventoryText(
            'app.inventory.position.floorplan.noCachedForBuildingFloor',
            'No cached floorplan is available for this building and floor.',
          )}
          existingPins={existingPins}
          floorplanDimensions={floorplanDimensions}
          floorplanImageUrl={floorplanImageUrl}
          floorplanRecord={floorplanRecord}
          isLoading={isLoadingFloorplan}
          isSavingFloorplan={isSavingFloorplan}
          loadingContent={
            <Typography.Text type="secondary">
              {translateInventoryText('app.inventory.position.floorplan.loading', 'Loading floorplan...')}
            </Typography.Text>
          }
          loadingMinHeight={280}
          onPickMarker={setSelectedMarker}
          onUpload={file => void handleFloorplanUpload(file)}
          selectedMarker={selectedMarker}
          showPinSummary={false}
          uploadError={floorplanUploadError}
        />

        <Flex gap="small" wrap>
          <Tag>
            {readBuildingNameLabel(position.building) ||
              translateInventoryText('app.inventory.position.building.none', 'No building')}
          </Tag>
          <Tag>{position.floor || translateInventoryText('app.inventory.position.floor.none', 'No floor')}</Tag>
          <FloorplanPinSummary existingPins={existingPins} selectedMarker={selectedMarker} />
        </Flex>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            {translateInventoryText(
              'app.inventory.position.floorplan.skipVisualPlacementLabel',
              'If visual placement is skipped',
            )}
          </Typography.Text>
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 4 }}
            onChange={event => setNotPlacedReason(event.target.value)}
            placeholder={translateInventoryText(
              'app.inventory.position.floorplan.notPlacedReasonPlaceholder',
              'Reason, e.g. no readable floorplan for this floor',
            )}
            value={notPlacedReason}
          />
        </Space>

        <Flex gap="small" justify="space-between" wrap>
          <Button onClick={onBack}>
            {translateInventoryText('app.inventory.position.actions.backToReview', 'Back to review')}
          </Button>
          <Space wrap>
            <Button disabled={Boolean(floorplanImageUrl) && !notPlacedReason.trim()} onClick={saveNotPlaced}>
              {floorplanImageUrl
                ? translateInventoryText('app.inventory.position.actions.saveNotPlaced', 'Save not placed')
                : translateInventoryText('app.inventory.position.actions.saveNoFloorplan', 'Save no floorplan')}
            </Button>
            <Button disabled={!(selectedMarker && floorplanImageUrl)} onClick={savePlaced} type="primary">
              {translateInventoryText('app.inventory.position.actions.saveMarker', 'Save marker')}
            </Button>
          </Space>
        </Flex>
      </Space>
    </Card>
  );
}
